import { useCallback, useRef, useState } from 'react'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useAsyncMutation } from './useAsyncMutation'

interface Args {
  tournamentId: Id<'tournaments'> | null | undefined
  userId: string
}

/**
 * Mini-controller for lobby chat send. Owns:
 *  - The controlled `text` state for the input.
 *  - Optimistic clear-on-send with restore-on-error so users don't lose
 *    what they typed if the mutation rejects.
 *  - `pending` flag for the disabled state on the input/button.
 *
 * Extracted from `TournamentLobbyScreen` so the screen doesn't have to
 * carry the chat plumbing in line with everything else (header card,
 * drawer wiring, cohost modal, tab strip, per-second tick, …).
 *
 * Returns a stable handle whose `text` / `pending` change but whose
 * function identities (`send`, `setText`) stay stable across renders
 * (via `useAsyncMutation` + `useCallback`).
 */
export function useLobbyChat({ tournamentId, userId }: Args) {
  const sendMessageMutation = useMutation(api.tournaments.mutations.sendTournamentMessage)
  const lastSentRef = useRef('')
  const [text, setText] = useState('')

  const sendMessage = useAsyncMutation(sendMessageMutation, {
    onError: () => {
      // Restore the user's text on failure. `setText((prev) => prev || lastSent)`
      // preserves anything the user has typed since the failed send (rare but
      // possible if they kept typing during the in-flight call).
      setText((prev) => prev || lastSentRef.current)
    }
  })

  const send = useCallback(
    async (trimmed: string) => {
      if (!tournamentId || !userId || !trimmed) return
      lastSentRef.current = trimmed
      // Optimistically clear the input; restored above on failure.
      setText('')
      const result = await sendMessage.run({ tournamentId, userId, text: trimmed })
      if (result !== undefined) {
        lastSentRef.current = ''
      }
    },
    [tournamentId, userId, sendMessage]
  )

  return { text, setText, send, pending: sendMessage.pending } as const
}
